import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

export type KrakenEntityType = 'item' | 'seller' | 'saw' | 'entity' | 'product' | 'seizure'

export interface KrakenEntity {
  type: KrakenEntityType
  id: string // Raw ID without prefix (for API calls)
  fullId: string // ID with prefix (for display/identification)
  name?: string
  syncedWithNeptune?: boolean
}

interface KrakenContextValue {
  currentEntity: KrakenEntity | null
  setItemContext: (itemId: string, name?: string, syncedWithNeptune?: boolean) => void
  setSellerContext: (sellerId: string, name?: string, syncedWithNeptune?: boolean) => void
  setSawContext: (sawId: string, name?: string, syncedWithNeptune?: boolean) => void
  setEntityContext: (entityId: string, name?: string, syncedWithNeptune?: boolean) => void
  setProductContext: (productId: string, name?: string, syncedWithNeptune?: boolean) => void
  clearContext: () => void

  isOpen: boolean
  openChat: (
    entity?: KrakenEntity | { type: KrakenEntityType; id: string; name?: string; syncedWithNeptune?: boolean },
  ) => void
  closeChat: () => void

  hidden: boolean
  // Increment/decrement a hide counter. Callers pair true (on mount/open)
  // with false (on unmount/close) so concurrent modals stack correctly.
  setHidden: (hide: boolean) => void
}

const KrakenContext = createContext<KrakenContextValue | null>(null)

const buildEntity = (
  type: KrakenEntityType,
  raw: string,
  name?: string,
  syncedWithNeptune?: boolean,
): KrakenEntity => {
  const prefix = `${type}_`
  const rawId = raw.startsWith(prefix) ? raw.slice(prefix.length) : raw
  const fullId = raw.startsWith(prefix) ? raw : `${prefix}${rawId}`
  return { type, id: rawId, fullId, name, syncedWithNeptune }
}

export const KrakenProvider = ({ children }: { children: ReactNode }) => {
  const [currentEntity, setCurrentEntity] = useState<KrakenEntity | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [hiddenCount, setHiddenCount] = useState(0)
  const pathnameAtSet = useRef<string | null>(null)
  const location = useLocation()

  // Auto-clear an imperatively-pushed entity when the user navigates away from
  // the route that pushed it. Without this, a stale setItemContext from a
  // closed drawer keeps winning over URL detection on the next page.
  useEffect(() => {
    if (currentEntity && pathnameAtSet.current && location.pathname !== pathnameAtSet.current) {
      setCurrentEntity(null)
      pathnameAtSet.current = null
    }
  }, [location.pathname, currentEntity])

  const setItemContext = useCallback(
    (itemId: string, name?: string, syncedWithNeptune?: boolean) => {
      setCurrentEntity(buildEntity('item', itemId, name, syncedWithNeptune))
      pathnameAtSet.current = location.pathname
    },
    [location.pathname],
  )

  const setSellerContext = useCallback(
    (sellerId: string, name?: string, syncedWithNeptune?: boolean) => {
      setCurrentEntity(buildEntity('seller', sellerId, name, syncedWithNeptune))
      pathnameAtSet.current = location.pathname
    },
    [location.pathname],
  )

  const setSawContext = useCallback(
    (sawId: string, name?: string, syncedWithNeptune?: boolean) => {
      setCurrentEntity(buildEntity('saw', sawId, name, syncedWithNeptune))
      pathnameAtSet.current = location.pathname
    },
    [location.pathname],
  )

  const setEntityContext = useCallback(
    (entityId: string, name?: string, syncedWithNeptune?: boolean) => {
      setCurrentEntity(buildEntity('entity', entityId, name, syncedWithNeptune))
      pathnameAtSet.current = location.pathname
    },
    [location.pathname],
  )

  const setProductContext = useCallback(
    (productId: string, name?: string, syncedWithNeptune?: boolean) => {
      setCurrentEntity(buildEntity('product', productId, name, syncedWithNeptune))
      pathnameAtSet.current = location.pathname
    },
    [location.pathname],
  )

  const clearContext = useCallback(() => {
    setCurrentEntity(null)
    pathnameAtSet.current = null
  }, [])

  const openChat = useCallback<KrakenContextValue['openChat']>(
    (entity) => {
      if (entity) {
        const built =
          'fullId' in entity
            ? entity
            : buildEntity(entity.type, entity.id, entity.name, entity.syncedWithNeptune)
        setCurrentEntity(built)
        pathnameAtSet.current = location.pathname
      }
      setIsOpen(true)
    },
    [location.pathname],
  )

  const closeChat = useCallback(() => setIsOpen(false), [])

  const setHidden = useCallback((hide: boolean) => {
    setHiddenCount((c) => Math.max(0, c + (hide ? 1 : -1)))
  }, [])

  const value = useMemo(
    () => ({
      currentEntity,
      setItemContext,
      setSellerContext,
      setSawContext,
      setEntityContext,
      setProductContext,
      clearContext,
      isOpen,
      openChat,
      closeChat,
      hidden: hiddenCount > 0,
      setHidden,
    }),
    [
      currentEntity,
      setItemContext,
      setSellerContext,
      setSawContext,
      setEntityContext,
      setProductContext,
      clearContext,
      isOpen,
      openChat,
      closeChat,
      hiddenCount,
      setHidden,
    ],
  )

  return <KrakenContext.Provider value={value}>{children}</KrakenContext.Provider>
}

export const useKrakenContext = () => {
  const context = useContext(KrakenContext)
  if (!context) {
    throw new Error('useKrakenContext must be used within a KrakenProvider')
  }
  return context
}

export const useKrakenContextOptional = () => {
  return useContext(KrakenContext)
}
